import { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Clock } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import allPosts, { categories } from "@/data/blogPosts";

export default function BlogPage() {
  const [activeCategory, setActiveCategory] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const visiblePosts = useMemo(() => {
    if (!activeCategory) return allPosts;
    return allPosts.filter((p) => p.category === activeCategory);
  }, [activeCategory]);

  const featured = visiblePosts[0];
  const rest = visiblePosts.slice(1);

  return (
    <div className="min-h-screen bg-[#030305] text-white" data-testid="blog-page">
      <Header />

      <main className="pt-28 lg:pt-32 pb-20">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
            className="max-w-[820px] mb-14"
          >
            <div className="font-mono-label text-[#D4FF4A]/70 mb-5">
              Field notes · from the ICPIntel team
            </div>
            <h1 className="font-display text-white text-[44px] lg:text-[64px] font-semibold leading-[1.02] tracking-[-0.03em] mb-5">
              Stories about the 10% that runs the business.
            </h1>
            <p className="text-lg text-[#A1A1AA] leading-[1.6] max-w-[640px]">
              Case studies from real companies, business-model breakdowns, and the AI
              playbook we use to find buyers worth chasing. New posts on roughly the
              same cadence the work happens.
            </p>
          </motion.div>

          <div
            className="flex flex-wrap items-center gap-2 mb-12"
            data-testid="blog-category-filters"
          >
            <FilterPill
              label="All"
              active={activeCategory === null}
              onClick={() => setActiveCategory(null)}
              testid="blog-filter-all"
            />
            {categories.map((cat) => (
              <FilterPill
                key={cat}
                label={cat}
                active={activeCategory === cat}
                onClick={() => setActiveCategory(cat)}
                testid={`blog-filter-${cat.toLowerCase().replace(/\s+/g, "-")}`}
              />
            ))}
          </div>

          {featured && (
            <FeaturedCard post={featured} />
          )}

          {rest.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8 mt-10">
              {rest.map((post, i) => (
                <PostCard key={post.slug} post={post} index={i} />
              ))}
            </div>
          )}

          {visiblePosts.length === 0 && (
            <div className="py-20 text-center">
              <div className="font-mono text-[#71717A] text-sm">
                No posts in this category yet.
              </div>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
}

function FilterPill({ label, active, onClick, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`text-[13px] px-4 py-1.5 rounded-full border transition-all ${
        active
          ? "border-[#D4FF4A] bg-[#D4FF4A]/10 text-[#D4FF4A]"
          : "border-white/[0.08] text-[#A1A1AA] hover:border-white/[0.2] hover:text-white"
      }`}
    >
      {label}
    </button>
  );
}

function FeaturedCard({ post }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 28 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
    >
      <Link
        to={`/blog/${post.slug}`}
        data-testid={`blog-card-${post.slug}`}
        className="group relative block bg-[#0A0A0F] border border-white/[0.08] rounded-2xl overflow-hidden hover:border-white/[0.18] transition-colors"
      >
        <div className="grid grid-cols-1 lg:grid-cols-[1.1fr_1fr]">
          <CoverPanel post={post} large />
          <div className="p-8 lg:p-12 flex flex-col justify-center">
            <div className="flex items-center gap-3 mb-5">
              <span className="font-mono-label text-[#D4FF4A]/80 text-[10px]">
                {post.category}
              </span>
              <span className="text-[#3F3F46]">·</span>
              <span className="font-mono text-[#71717A] text-[10px] flex items-center gap-1.5">
                <Clock size={11} />
                {post.readTime} min read
              </span>
            </div>
            <h2 className="font-display text-white text-[28px] lg:text-[36px] font-semibold leading-[1.15] tracking-[-0.02em] mb-4 group-hover:text-[#D4FF4A] transition-colors">
              {post.title}
            </h2>
            <p className="text-[#A1A1AA] text-[15px] leading-[1.6] mb-7 max-w-[480px]">
              {post.excerpt}
            </p>
            <div className="flex items-center gap-3">
              <div className="text-white text-sm font-medium">{post.author}</div>
              <span className="text-[#3F3F46]">·</span>
              <div className="font-mono text-[#71717A] text-xs">{post.publishedAt}</div>
            </div>
            <div className="mt-7 inline-flex items-center gap-2 text-[#D4FF4A] text-sm font-medium">
              Read post
              <ArrowRight
                size={16}
                className="transition-transform group-hover:translate-x-1"
              />
            </div>
          </div>
        </div>
      </Link>
    </motion.div>
  );
}

function PostCard({ post, index }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{
        duration: 0.6,
        delay: 0.1 + index * 0.08,
        ease: [0.16, 1, 0.3, 1],
      }}
    >
      <Link
        to={`/blog/${post.slug}`}
        data-testid={`blog-card-${post.slug}`}
        className="group block bg-[#0A0A0F] border border-white/[0.08] rounded-2xl overflow-hidden hover:border-white/[0.18] transition-colors h-full"
      >
        <CoverPanel post={post} />
        <div className="p-7">
          <div className="flex items-center gap-3 mb-4">
            <span className="font-mono-label text-[#D4FF4A]/80 text-[10px]">
              {post.category}
            </span>
            <span className="text-[#3F3F46]">·</span>
            <span className="font-mono text-[#71717A] text-[10px] flex items-center gap-1.5">
              <Clock size={11} />
              {post.readTime} min
            </span>
          </div>
          <h3 className="font-display text-white text-[22px] font-semibold leading-[1.2] tracking-[-0.01em] mb-3 group-hover:text-[#D4FF4A] transition-colors">
            {post.title}
          </h3>
          <p className="text-[#A1A1AA] text-[14px] leading-[1.6] mb-6 line-clamp-3">
            {post.excerpt}
          </p>
          <div className="pt-5 border-t border-white/[0.06] flex items-center justify-between">
            <div className="text-[#A1A1AA] text-xs">
              {post.author} · {post.publishedAt}
            </div>
            <ArrowRight
              size={14}
              className="text-[#71717A] group-hover:text-[#D4FF4A] transition-colors"
            />
          </div>
        </div>
      </Link>
    </motion.div>
  );
}

function CoverPanel({ post, large = false }) {
  return (
    <div
      className={`relative overflow-hidden ${
        large ? "aspect-[5/4] lg:aspect-auto lg:min-h-[420px]" : "aspect-[16/9]"
      } bg-[#06060A]`}
    >
      <div
        className={`absolute inset-0 bg-gradient-to-br ${post.coverGradient}`}
      />
      <div
        aria-hidden
        className="absolute inset-0 opacity-[0.06]"
        style={{
          backgroundImage:
            "linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px)",
          backgroundSize: "32px 32px",
        }}
      />
      <div className="relative h-full flex flex-col justify-between p-7 lg:p-10">
        <div className="flex items-start justify-between gap-4">
          <div className="font-mono-label text-[#D4FF4A]/70 text-[10px]">
            {post.coverEyebrow}
          </div>
          {post.coverLogo && (
            <img
              src={post.coverLogo}
              alt=""
              aria-hidden="true"
              className={large ? "h-9 lg:h-11 w-auto" : "h-7 w-auto"}
            />
          )}
        </div>
        <div
          className={`font-display text-white font-semibold leading-[1] tracking-[-0.03em] ${
            large
              ? "text-[56px] lg:text-[88px]"
              : "text-[40px] lg:text-[56px]"
          }`}
          style={{ textShadow: "0 0 60px rgba(212, 255, 74, 0.15)" }}
        >
          {post.coverHeadline}
        </div>
      </div>
    </div>
  );
}
